import React, { useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { withRouter } from 'react-router';
import { withBooking } from 'components/queries/GetBookingQuery';
import { withUser } from 'components/queries/GetUserQuery';
import { parse } from 'query-string';
import { Row, Column } from 'components/shared/Layout';
import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { locationShape, bookingShape, userShape } from 'constants/propTypes';
import flowForwardQuery from './flowForwardQuery';
import FlowForward from './FlowForward';
import { GET_SEATING_DATA } from 'store/queries';
import { PLUS, STANDARD } from 'constants/classOfService';
import { isSeatAvailable } from 'utils/seating/seat';
import moment from 'moment';

const getBookingItemRefsFromParams = (search) => {
  const { itemRefs = [] } = parse(search);

  return itemRefs.split(',').map(Number);
};

const getAllowedCOS = {
  STANDARD: [STANDARD],
  PLUS: [STANDARD, PLUS]
};

export const Index = ({ booking: { reference, status, outbound, inbound, currency }, location: { search }, user }) => {
  const exchangeItems = getBookingItemRefsFromParams(search);

  const exchangeSegment = outbound
    .concat(inbound)
    .find((segment) => segment.products.find((prod) => exchangeItems.includes(Number(prod.itemRef))));

  const { loading, data, error } = useQuery(flowForwardQuery, {
    fetchPolicy: 'network-only',
    variables: {
      reference,
      exchangeItems,
      currency
    }
  });

  const [getSeatingData, { loading: getSeatingDataLoading }] = useLazyQuery(GET_SEATING_DATA);

  const [ffDataWithSeats, setFfDataWithSeats] = useState(null);

  useEffect(() => {
    (async () => {
      if (!data?.flowForwardSearch) return;

      const base = data.flowForwardSearch;
      const direction = exchangeSegment.direction === 'outbound' ? 'outbound' : 'inbound';
      const journeys = base[direction]?.journeys ?? [];
      const inventoryClass = exchangeSegment.seats?.[0]?.inventoryClass;
      const numberOfTickets = exchangeSegment.activeBookingItems.length;
      const now = moment();
      const fortyFiveMinutesFromNow = moment().add(45, 'minutes');
      const currentSegmentTime = moment(exchangeSegment.timing.isoDate);

      // Clone the journeys and fares with the extra property
      const newJourneys = await Promise.all(
        journeys.map(async (j) => {
          const departs = moment(j.timing.departs, 'HH:mm');

          const newFares = await Promise.all(
            j.fares.map(async (f) => {
              // Only include matching fares
              const matches =
                getAllowedCOS[exchangeSegment.classOfService.code].includes(f.classOfService?.code) &&
                f.canBeBooked &&
                (f.bookingItems?.length ?? 0) === 0 &&
                departs.isSame(now, 'day') &&
                departs.isAfter(fortyFiveMinutesFromNow) &&
                departs.isBefore(currentSegmentTime) &&
                f.legs?.[0];

              if (!matches) return f; // leave untouched

              const leg0 = f.legs[0];
              const seatingDataResponse = await getSeatingData({
                variables: {
                  serviceId: leg0.serviceName,
                  origin: leg0.origin?.uic,
                  destination: leg0.destination?.uic,
                  travelDate: leg0.timing?.departureDate
                }
              });

              const seatingData = seatingDataResponse.data.getSeatingData;

              const seats = seatingData.carriages.reduce(
                (acc, carriage) =>
                  acc.concat(
                    carriage.seats.map((seat) => ({
                      ...seat,
                      carriageNumber: carriage.carriageNumber,
                      carriageSequenceNumber: carriage.sequenceNumber
                    }))
                  ),
                []
              );

              const availableSeats = seats
                .filter((seat) => isSeatAvailable(seat) && seat.inventoryClass === inventoryClass)
                .sort((a, b) => {
                  if (a.carriageSequenceNumber > b.carriageSequenceNumber) {
                    return 1;
                  } else if (a.carriageSequenceNumber < b.carriageSequenceNumber) {
                    return -1;
                  } else {
                    return a.sequenceNumber > b.sequenceNumber ? 1 : -1;
                  }
                });

              if (availableSeats.length >= numberOfTickets) {
                const carriagesWithEnoughSeats = seatingData.carriages.filter(
                  (carriage) => availableSeats.filter((seat) => seat.carriageNumber === carriage.carriageNumber).length >= numberOfTickets
                );

                let bestSeatsOptions = availableSeats;

                if (carriagesWithEnoughSeats.length > 0) {
                  bestSeatsOptions = availableSeats.filter((seat) => seat.carriageNumber === carriagesWithEnoughSeats[0].carriageNumber);
                }

                return {
                  ...f,
                  cappedTrain: {
                    trainNumber: leg0.serviceName,
                    serviceIdentifier: leg0.serviceIdentifier,
                    exchangeItems,
                    recommendedSeats: bestSeatsOptions.slice(0, numberOfTickets).map((seat) => ({
                      coach: seat.carriageNumber,
                      number: seat.number
                    }))
                  }
                };
              }

              return { ...f };
            })
          );

          return { ...j, fares: newFares };
        })
      );

      // Build new ffData object with replaced journeys
      const newFfData = {
        ...base,
        [direction]: {
          ...base[direction],
          journeys: newJourneys
        }
      };

      setFfDataWithSeats(newFfData);
    })();
  }, [data, exchangeSegment, getSeatingData]);

  if (loading || getSeatingDataLoading) {
    return (
      <Row height="100vh">
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/manage/bookings/${reference}/journeys`
    });
  }

  return (
    <FlowForward
      searchResults={ffDataWithSeats || data?.flowForwardSearch}
      bookingStatus={status}
      reference={reference}
      currency={currency}
      exchangeSegment={exchangeSegment}
      user={user}
    />
  );
};

Index.propTypes = {
  booking: bookingShape,
  location: locationShape,
  user: userShape
};

export default withUser(withBooking(withRouter(Index)));
